import { Button, Card, CardContent, CardHeader, CardTitle } from "@exhousing/design-system";
import { ImagePlus, Trash2 } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";

import {
  useDeleteMedia,
  usePropertyMedia,
  useUploadMedia,
} from "@/hooks/api/useMedia";

export function MediaGallery({ propertyId }: { propertyId: string }) {
  const { data: media, isLoading } = usePropertyMedia(propertyId);
  const upload = useUploadMedia(propertyId);
  const remove = useDeleteMedia(propertyId);
  const inputRef = useRef<HTMLInputElement>(null);

  const onFiles = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((file) =>
      upload.mutate(file, {
        onError: () => toast.error(`Could not upload ${file.name}`),
      }),
    );
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Photos</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
        >
          <ImagePlus className="size-4" />
          {upload.isPending ? "Uploading…" : "Add photos"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => onFiles(e.target.files)}
        />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : media && media.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {media.map((m) => (
              <div
                key={m.id}
                className="group relative aspect-[4/3] overflow-hidden rounded-md border border-border bg-muted"
              >
                <img
                  src={m.thumbnail_url ?? m.url}
                  alt={m.caption ?? m.original_filename}
                  className="size-full object-cover"
                  loading="lazy"
                />
                <button
                  onClick={() => remove.mutate(m.id)}
                  className="absolute right-1.5 top-1.5 hidden rounded-md bg-background/90 p-1.5 text-danger shadow-sm group-hover:block"
                  aria-label="Delete photo"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No photos yet. Add the first one.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
